import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { ChevronLeft, ChevronRight, RotateCcw, Play, Pause } from 'lucide-react';
import { useSecondScreenData } from '../../../contexts/SecondScreenContext';
import { getCardsByPhase, getStandaloneCards, getCardsByDeck } from '../../../data/promptCards';
import type { PromptCard } from '../../../data/promptCards';
import type { WidgetProps } from '../../../types/second-screen.types';

const AUTO_CYCLE_INTERVAL = 12000; // 12 seconds per card

const PromptCardWidget: React.FC<WidgetProps> = ({ config }) => {
  const { state, theme } = useSecondScreenData();
  const isDark = theme === 'dark';

  const currentPhaseIndex = state?.currentPhaseIndex ?? 0;

  // Which deck to show: 'phase' (auto-follows current phase), or a specific deckId
  const deckMode = config?.deckMode || 'phase';

  const cards: PromptCard[] = useMemo(() => {
    if (deckMode === 'phase') return getCardsByPhase(currentPhaseIndex);
    if (deckMode === 'standalone') return getStandaloneCards();
    const deckId = parseInt(deckMode, 10);
    if (!isNaN(deckId)) return getCardsByDeck(deckId);
    return getCardsByPhase(currentPhaseIndex);
  }, [deckMode, currentPhaseIndex]);

  const [cardIndex, setCardIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [isAutoCycle, setIsAutoCycle] = useState(true);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Reset index when cards change
  useEffect(() => {
    setCardIndex(0);
    setIsFlipped(false);
  }, [cards]);

  // Auto-cycle
  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (isAutoCycle && cards.length > 1) {
      timerRef.current = setInterval(() => {
        setCardIndex(prev => (prev + 1) % cards.length);
        setIsFlipped(false);
      }, AUTO_CYCLE_INTERVAL);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isAutoCycle, cards.length]);

  const goNext = useCallback(() => {
    setCardIndex(prev => (prev + 1) % cards.length);
    setIsFlipped(false);
  }, [cards.length]);

  const goPrev = useCallback(() => {
    setCardIndex(prev => (prev - 1 + cards.length) % cards.length);
    setIsFlipped(false);
  }, [cards.length]);

  if (!state || cards.length === 0) {
    return (
      <div className={`flex items-center justify-center h-full text-sm ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
        No cards for current phase
      </div>
    );
  }

  const card = cards[cardIndex];

  return (
    <div className="flex flex-col h-full p-3 gap-2">
      {/* Header: deck name + controls */}
      <div className="flex items-center justify-between shrink-0">
        <span className={`text-xs font-semibold uppercase tracking-wider ${
          isDark ? 'text-white/40' : 'text-slate-400'
        }`}>
          {card.deckTitle} · {card.cardCategory}
        </span>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setIsAutoCycle(!isAutoCycle)}
            className={`p-1 rounded transition-colors ${isDark ? 'hover:bg-white/10 text-white/40' : 'hover:bg-slate-100 text-slate-400'}`}
            title={isAutoCycle ? 'Pause auto-cycle' : 'Start auto-cycle'}
          >
            {isAutoCycle ? <Pause size={12} /> : <Play size={12} />}
          </button>
          <span className={`text-xs font-mono ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
            {cardIndex + 1}/{cards.length}
          </span>
        </div>
      </div>

      {/* Card with flip */}
      <div
        className="flex-1 min-h-0 cursor-pointer perspective-1000"
        onClick={() => setIsFlipped(!isFlipped)}
      >
        <div
          className="relative w-full h-full transition-transform duration-500"
          style={{
            transformStyle: 'preserve-3d',
            transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
          }}
        >
          {/* Front - Prompt */}
          <div
            className={`absolute inset-0 rounded-xl p-4 flex flex-col justify-center items-center text-center border ${
              isDark
                ? 'bg-gradient-to-br from-primary/20 to-primary/5 border-primary/30'
                : 'bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20'
            }`}
            style={{ backfaceVisibility: 'hidden' }}
          >
            <p className={`text-sm font-medium leading-relaxed ${isDark ? 'text-white/90' : 'text-slate-800'}`}>
              {card.prompt}
            </p>
            <span className={`text-xs mt-3 ${isDark ? 'text-white/20' : 'text-slate-300'}`}>
              Click to flip
            </span>
          </div>

          {/* Back - Facilitation Guide */}
          <div
            className={`absolute inset-0 rounded-xl p-4 flex flex-col justify-center items-center text-center border ${
              isDark
                ? 'bg-gradient-to-br from-amber-500/20 to-amber-500/5 border-amber-500/30'
                : 'bg-gradient-to-br from-amber-500/10 to-amber-500/5 border-amber-500/20'
            }`}
            style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
          >
            <span className={`text-xs font-semibold uppercase tracking-wider mb-2 ${
              isDark ? 'text-amber-400/60' : 'text-amber-600/60'
            }`}>
              Facilitation Guide
            </span>
            <p className={`text-xs leading-relaxed ${isDark ? 'text-white/70' : 'text-slate-700'}`}>
              {card.facilitationGuide}
            </p>
            <span className={`text-xs mt-3 ${isDark ? 'text-white/20' : 'text-slate-300'}`}>
              Click to flip back
            </span>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-center gap-3 shrink-0">
        <button
          onClick={goPrev}
          className={`p-1.5 rounded-full transition-colors ${
            isDark ? 'hover:bg-white/10 text-white/40' : 'hover:bg-slate-100 text-slate-400'
          }`}
        >
          <ChevronLeft size={16} />
        </button>
        <button
          onClick={() => { setCardIndex(0); setIsFlipped(false); }}
          className={`p-1.5 rounded-full transition-colors ${
            isDark ? 'hover:bg-white/10 text-white/40' : 'hover:bg-slate-100 text-slate-400'
          }`}
        >
          <RotateCcw size={14} />
        </button>
        <button
          onClick={goNext}
          className={`p-1.5 rounded-full transition-colors ${
            isDark ? 'hover:bg-white/10 text-white/40' : 'hover:bg-slate-100 text-slate-400'
          }`}
        >
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
};

export default PromptCardWidget;
